// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Beeline — turning a seed into a hidden country, deterministically. This is the
// whole P2P-sync surface: every peer feeds the frozen lobby seed through here and
// gets the byte-identical mystery, so nothing about the answer crosses the wire.

import { makeRng, randInt, hashSeed } from '@ben-gy/game-engine/rng';
import { answerPool, type Mode } from './modes';
import type { Country } from './data';

/** Today's date in UTC as YYYY-MM-DD — the same string for every player on Earth. */
export function todayUTC(d: Date = new Date()): string {
  return d.toISOString().slice(0, 10);
}

/** The daily seed for a mode: identical worldwide, changes at UTC midnight. */
export function dailySeed(mode: Mode, dateStr: string): number {
  return hashSeed(`beeline:${mode.id}:${dateStr}`);
}

/**
 * The hidden country for a (mode, seed). Pure: the seed picks an index into the
 * mode's answer pool. Because the pool is a deterministic slice of the bundled
 * data, two peers with the same seed choose the same country without exchanging
 * a byte about it.
 */
export function mysteryFor(mode: Mode, seed: number): Country {
  const pool = answerPool(mode);
  const rng = makeRng(seed >>> 0);
  return pool[randInt(rng, 0, pool.length - 1)];
}
